import Decimal from 'decimal.js';
import { labelizeLedgerKind, labelizeSourcePlan } from './ledger-labels';
import type { TenantFeatures } from './tenant-features';

/**
 * JM-EX9: pure helpers for the in-app notification system. Kept in
 * `@mlm/shared` because they have zero infrastructure dependencies and are
 * exercised by both the worker (fan-out) and unit tests in the API package.
 */

type LedgerCredit = {
  id: string;
  slot_id: string;
  user_id: string;
  amount: string;
  currency_kind: 'cash' | 'gc' | 'token' | 'coin' | 'ai_credit';
  kind?: string | null;
  source_plan?: string | null;
  source_event_id?: string | null;
  /** Carries `trigger_slot_id` (stamped by `runEvent`) used to label who triggered the credit. */
  metadata?: Record<string, unknown> | null;
};

/**
 * Render a notification template with placeholder substitution. Unknown
 * placeholders are left as-is so an admin typo is visible (vs. silently
 * dropped to empty).
 */
export function renderNotificationTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, name) => {
    return Object.prototype.hasOwnProperty.call(vars, name) ? vars[name]! : match;
  });
}

/**
 * Resolve an admin-templated member notification for an event code, rendering
 * its title/body with `vars`. Returns null when the event is unknown or
 * disabled (the caller then fires nothing). Used by the API trigger sites
 * (order placed, payment submitted, cash-out, …).
 */
export function buildEventNotification(
  features: TenantFeatures,
  eventCode: string,
  vars: Record<string, string>,
): { title: string; body: string } | null {
  const tpl = features.notification_templates.events?.[eventCode];
  if (!tpl || !tpl.enabled) return null;
  return {
    title: renderNotificationTemplate(tpl.title, vars),
    body: renderNotificationTemplate(tpl.body, vars),
  };
}

/**
 * Map a ledger currency_kind onto the user-facing currency word. `coinLabel`
 * comes from `tenant_config.features.coin_label` so tenants can rename
 * "Coins" → "Gems"/"Points".
 */
export function currencyWord(currency: LedgerCredit['currency_kind'], coinLabel: string): string {
  switch (currency) {
    case 'cash':
      return 'Cash';
    case 'gc':
      return 'GC';
    case 'coin':
      return coinLabel;
    case 'token':
      return 'Tokens';
    case 'ai_credit':
      return 'AI Credits';
  }
}

/**
 * Format the amount for display. Cash uses 2 dp + comma thousands; scrip
 * currencies (GC / Coin / Token) use up to 4 dp without a currency symbol
 * since the symbol comes from the template's `{currency}` slot.
 */
export function formatNotificationAmount(
  amount: string | Decimal,
  currency: LedgerCredit['currency_kind'],
): string {
  const n = new Decimal(amount);
  if (currency === 'cash') {
    return n.toNumber().toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return n.toNumber().toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/**
 * Build the rows to insert into `notifications` for one drain batch of
 * ledger credits. Returns one row per CASH/GC/COIN/TOKEN credit row that the
 * template configuration says should emit. Caller does the actual insert in
 * the same tx as the wallet upsert so a re-drain is impossible (the cursor
 * has already advanced).
 */
export function buildEarningsNotifications(
  credits: LedgerCredit[],
  features: TenantFeatures,
  /**
   * Optional `trigger_slot_id` → display-name map. The worker resolves the
   * triggering slot's owner name so `{trigger_user}` renders a real name
   * instead of a dash. Unknown/absent → '—'.
   */
  triggerNames?: Map<string, string>,
): Array<{
  userId: string;
  kind: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
}> {
  const cfg = features.notification_templates.earnings;
  if (!cfg.enabled) return [];

  const out: Array<{
    userId: string;
    kind: string;
    title: string;
    body: string;
    metadata: Record<string, unknown>;
  }> = [];

  for (const row of credits) {
    const amount = new Decimal(row.amount);
    if (amount.lte(0)) continue;

    const sourcePlan = row.source_plan ?? '';
    const ledgerKind = row.kind ?? '';
    // The triggering slot (buyer / newly-activated member) is stamped onto the
    // ledger metadata by `runEvent`; the worker passes a name map so this
    // renders a real person instead of '—'.
    const triggerSlotId =
      typeof row.metadata?.trigger_slot_id === 'string'
        ? (row.metadata.trigger_slot_id as string)
        : null;
    const triggerUser = (triggerSlotId && triggerNames?.get(triggerSlotId)) || '—';
    const vars: Record<string, string> = {
      amount: formatNotificationAmount(row.amount, row.currency_kind),
      currency: currencyWord(row.currency_kind, features.coin_label),
      source_plan: sourcePlan
        ? labelizeSourcePlan(sourcePlan, features.ledger_source_plan_labels)
        : '—',
      kind: ledgerKind ? labelizeLedgerKind(ledgerKind, features.ledger_labels) : '—',
      trigger_user: triggerUser,
    };

    out.push({
      userId: row.user_id,
      kind: 'earnings',
      title: renderNotificationTemplate(cfg.title, vars),
      body: renderNotificationTemplate(cfg.body, vars),
      metadata: {
        amount: row.amount,
        currency: row.currency_kind,
        source_plan: sourcePlan || null,
        ledger_kind: ledgerKind || null,
        ledger_id: row.id,
        source_event_id: row.source_event_id ?? null,
        slot_id: row.slot_id,
      },
    });
  }

  return out;
}
